import React, {useState, createContext, use, useEffect} from "react";
import PropTypes from "prop-types";
import {useLocation} from "react-router-dom";
import {useFormContext} from "react-hook-form";
import {useQuery} from "@apollo/client";
import {GET_INTEGRATIONS} from "../../../graphql/integrationQueries";
import {ALERT} from "../../../resources/environment";
import {emitStartStopMetric} from "../../../resources/metrics";
import {handleError} from "../../../resources/utils/error";
import {integrationData, integrationCategoryInfo} from "../Data/integrationData";
import {useAuth} from "../../../components/authentication/Context/AuthProvider";
import {deepFindValueForKey} from "../../../resources/utils/objectUtils";
import {each, find, findLastIndex, findWhere, isEmpty, isObject, map} from "underscore";

export const IntegrationsContext = createContext(null);

export const IntegrationsProvider = ({children}) => {
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [integrations, setIntegrations] = useState(integrationData);
  const [initialized, setInitialized] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState();
  const [selectedFormData, setSelectedFormData] = useState(undefined);
  const [searchText, setSearchText] = useState("");
  const location = useLocation();
  const {altAuthProviders} = useAuth();
  const validIntegrationPaths = integrations.map(({name}) => `/integrations/${name}`);
  const integrationFormMethods = useFormContext();

  const initializeIntegrationFields = (data = integrations) => {
    const currentIntegrations = [...integrations];
    data?.integrations?.map((integration) => {
      const match = findWhere(currentIntegrations, {name: integration?.name});
      if (!match) return;
      data?.[integration.name]?.inputFields?.map((field) => {
        const name = field.name;
        const value = integration.configuration[name];
        match.configuration[name] = value === "false" ? false : value;
      });
    });

    const updateAuthProvidersStatus = () => {
      return map(currentIntegrations, (integration, index) => {
        const authProvider = find(altAuthProviders, (provider) => {
          const integrationName = `${integration.name}`.toLowerCase();
          const matchedByName = [
            `${provider.displayName}`.toLowerCase(),
            `${provider.name}`.toLowerCase(),
          ].includes(integrationName);
          const matchedByType =
            integrationName === "genericsaml" &&
            integrationName === `${provider?.type}`.toLowerCase();
          return matchedByName || matchedByType;
        });

        if (!authProvider) return;
        currentIntegrations[index].configuration.isEnabled = !isEmpty(authProvider?.url);
      });
    };

    updateAuthProvidersStatus();

    const xsoarIndex = findLastIndex(currentIntegrations, {name: "xsoar"});
    if (xsoarIndex >= 0 && isObject(currentIntegrations[xsoarIndex]?.configuration)) {
      currentIntegrations[xsoarIndex].configuration.isEnabled =
        !!currentIntegrations[xsoarIndex].configuration.apiKey;
    }

    const revelstokeIndex = findLastIndex(currentIntegrations, {
      name: "revelstoke",
    });
    if (revelstokeIndex >= 0 && isObject(currentIntegrations[revelstokeIndex]?.configuration)) {
      currentIntegrations[revelstokeIndex].configuration.isEnabled =
        !!currentIntegrations[revelstokeIndex].configuration.apiKey;
    }

    const fireHydrantIndex = findLastIndex(currentIntegrations, {
      name: "firehydrant",
    });
    if (fireHydrantIndex >= 0 && isObject(currentIntegrations[fireHydrantIndex]?.configuration)) {
      const fireHydrantIntegration = find(data?.integrations, {
        name: "firehydrant",
      });
      if (fireHydrantIntegration?.configuration?.apiToken) {
        currentIntegrations[fireHydrantIndex].configuration.apiToken =
          fireHydrantIntegration.configuration.apiToken;
      }
      if (fireHydrantIntegration?.configuration?.isEnabled) {
        currentIntegrations[fireHydrantIndex].configuration.isEnabled =
          fireHydrantIntegration.configuration.isEnabled;
      }
    }

    const serviceNowIndex = findLastIndex(currentIntegrations, {
      name: "servicenow",
    });
    if (
      serviceNowIndex >= 0 &&
      isObject(currentIntegrations[serviceNowIndex]?.configuration)
    ) {
      const serviceNowIntegration = find(data?.integrations, {
        name: "servicenow",
      });
      if (serviceNowIntegration?.configuration?.clientId) {
        currentIntegrations[serviceNowIndex].configuration.clientId =
          serviceNowIntegration.configuration.clientId;
      }
      if (serviceNowIntegration?.configuration?.clientSecret) {
        currentIntegrations[serviceNowIndex].configuration.clientSecret =
          serviceNowIntegration.configuration.clientSecret;
      }
      if (serviceNowIntegration?.configuration?.instance) {
        currentIntegrations[serviceNowIndex].configuration.instance =
          serviceNowIntegration.configuration.instance;
      }
      if (serviceNowIntegration?.configuration?.isEnabled) {
        currentIntegrations[serviceNowIndex].configuration.isEnabled =
          serviceNowIntegration.configuration.isEnabled;
      }
    }

    setIntegrations(currentIntegrations);
    setInitialized(true);
  };

  const {loading, error, data, refetch} = useQuery(GET_INTEGRATIONS);

  useEffect(() => {
    if (data) {
      initializeIntegrationFields(data);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      handleError(error, setSubmitting);
    }
  }, [error]);

  const setIntegrationFromPath = (path) => {
    const integrationName = path.split("/")[2];
    const integrationFromPath = findWhere(integrations, {
      name: integrationName,
    });
    setSelectedIntegration(integrationFromPath);
  };

  useEffect(() => {
    if (!data || !validIntegrationPaths.includes(location.pathname)) {
      return;
    }
    setIntegrationFromPath(location.pathname);
  }, [loading, data]);

  useEffect(() => {
    if (!isEmpty(data) && selectedIntegration) {
      setSelectedFormData({...selectedIntegration.configuration});
    }
  }, [selectedIntegration, data, loading]);

  const updateIntegrationFields = (
    configuration,
    isDeleting = false,
    useNewPath = false,
    integrationName = null,
  ) => {
    const currentIntegrations = [...integrations];

    const name = selectedIntegration?.name;
    const deepName = deepFindValueForKey(configuration, "name");
    const nameToUse = useNewPath ? deepName : name || integrationName;
    if (!nameToUse) return;

    const match = findWhere(currentIntegrations, {name: nameToUse});

    const deepConfiguration = deepFindValueForKey(configuration, "configuration");
    if (useNewPath) {
      match.configuration = {
        ...match.configuration,
        ...(deepConfiguration ?? {}),
      };
    } else {
      match.configuration = configuration;
    }

    setIntegrations(currentIntegrations);

    if (match && integrationName && match.name === nameToUse) {
      setSelectedFormData({...match.configuration});
      if (integrationFormMethods?.setValue) {
        each(match.configuration, (value, key) => {
          integrationFormMethods.setValue(key, value);
        });
      }
    }

    refetch();
    const action = isDeleting ? "deleted" : "updated";
    const titleToDisplay = selectedIntegration?.title ?? "This";
    ALERT.success(`${titleToDisplay} integration configuration ${action} successfully`);
    isDeleting ? setDeleting(false) : setSubmitting(false);
  };

  const initializeForm = (schema, defaultValues, onSubmit, onDelete) => {
    selectedIntegration.schema = schema;
    selectedIntegration.defaultValues = defaultValues;
    selectedIntegration.onSubmit = async (formData) => {
      await onSubmit({variables: formData});
    };
    selectedIntegration.onDelete = async () => {
      await onDelete();
    };
  };

  useEffect(() => {
    return emitStartStopMetric("view", {location});
  }, []);

  return (
    <IntegrationsContext
      value={{
        submitting,
        setSubmitting,
        deleting,
        setDeleting,
        integrations,
        integrationCategoryInfo,
        selectedIntegration,
        setSelectedIntegration,
        loading: loading && !initialized,
        error,
        updateIntegrationFields,
        initializeForm,
        integrationFormMethods,
        selectedFormData,
        setSelectedFormData,
        searchText,
        setSearchText,
        altAuthProviders,
      }}
    >
      {children}
    </IntegrationsContext>
  );
};

IntegrationsProvider.propTypes = {
  children: PropTypes.any,
};

export const useIntegrations = () => {
  return use(IntegrationsContext);
};
